import React, { useEffect, useState } from 'react';
import { Theme } from '@mui/material';

import { makeStyles } from 'tss-react/mui';
import { TFunction, useTranslation } from 'react-i18next';
import {
  BlankState,
  Box,
  ContentTemplate,
  Grid,
  KeyValue,
  Link,
  MUIDataTable,
  MUIDataTableOptions,
  Progress,
  TableActionTypes,
  TitleHeader,
  Typography,
  useDataTable,
} from 'web-library';
import { RouteComponentProps, useHistory, useParams } from 'react-router-dom';
import IconWithLabel from 'web-library/stories/atom/IconWithLabel/IconWithLabel';
import { IconName } from 'web-library/stories/atom/Icon/Icon';

import RouteKey from '../../../routes/key';
import NavigationButtons from '../../../components/NavigationButtons';
import { candidateService } from '../../../api';
import { useAccessArrangementSummaryColumns } from './logic/useAccessArrangemenSummaryTableData';
import ApplicationDetails from './ApplicationDetails';
import ReviewApplication from './ReviewApplication';
import { usePostAccessArrangementAmendment } from '../../../api-refactored/candidate/usePostAccessArrangementAmendment';
import DocumentBadge from '../../../components/DocumentBadge';
import { useGetArrangementOutcomePDF } from '../../../api-refactored/candidate/useGetArrangementOutcomePDF';
import { AccessArrangmentGetSummaryDetails } from '../../../api/candidate/access-arrangement/model';
import { FileUploadState } from '../../../models/File';
import DownloadProgressModal from '../../../components/modals/DownloadProgressModal';
import { useWOGAA } from '../../../hooks/wogaa';

const { root, ammendSelectCondition, coordinatorRoot, coordinatorAmendSelectCondition } = RouteKey[
  'access-arrangement'
].child;
const { accessArrangement } = RouteKey.child;

// TODO jss-to-tss-react codemod: usages of this hook outside of this file will not be converted.
export const useStyles = makeStyles()((theme) =>
({
  header: {
    marginBottom: theme.spacing(6),
  },
  spaceBottom: {
    marginBottom: theme.spacing(2),
  },
  subheader: {
    marginBottom: theme.spacing(4),
  },
  keyValueContainer: {
    marginTop: theme.spacing(2),
  },
  applicationOutcomeContainer: {
    marginTop: theme.spacing(2),
  },
  applicationOutcomeRowContainer: {
    marginTop: theme.spacing(1.5),
    wordWrap: 'break-word',
    alignItems: 'center',
  },
  documentsContainer: {
    marginTop: theme.spacing(1.5),
  },
  documentBadgeContainer: {
    marginTop: theme.spacing(4),
  },
  documentOutcomeBadgeContainer: {
    marginTop: theme.spacing(1.5),
  },
  contentTemplateChildContainer: {
    marginBottom: theme.spacing(4),
  },

  personalDetailsContainer: {
    marginBottom: theme.spacing(3),
  },
  errorText: {
    color: theme.color.state.stateError,
    marginTop: theme.spacing(4),
    marginButton: theme.spacing(3),
  },
  examLevel: {
    color: theme.color.text.textHeader,
  },
  downloadPdf: {
    display: 'flex',
    marginTop: theme.spacing(1.3),
    paddingLeft: theme.spacing(3),
  },
  dropped: {
    marginLeft: theme.spacing(1),
    color: theme.color.state.stateError,
  },
}));

const getTableOptions = (classes: Record<string,string>, t: TFunction) => {
  const tableOptions: MUIDataTableOptions = {
    textLabels: {
      body: {
        noMatch: (
          <>
            <Box className={classes.emptyReserve}>
              <></>
            </Box>
            <Box className={classes.emptyContainer}>
              <BlankState label={t('accessArrangement->>AccessArrangementRequest.Empty')} />
            </Box>
          </>
        ),
      },
    },
    sort: true,
    setRowProps: () => ({ style: { verticalAlign: 'text-top' } }),
  };
  return tableOptions;
};

const handleAmendApplication = (
  isGroup: boolean,
  history: RouteComponentProps['history'],
  uuid: string,
  data?: AccessArrangmentGetSummaryDetails
) => {
  if (!isGroup) {
    history.push(`/${accessArrangement}/${root.route}/${ammendSelectCondition.route}/${uuid}`);
  } else {
    localStorage.setItem(
      `candidateNameAmendmentApplication: ${uuid}`,
      data?.accessArrangementRequestReviewModel.accessArrangementCandidateDetailsModel
        ?.statutoryName || ''
    );
    history.push(
      `/${accessArrangement}/${coordinatorRoot.route}/${coordinatorAmendSelectCondition.route}/${uuid}`
    );
  }
};

interface SummaryPageProps {
  isGroup: boolean;
}

const SummaryPage = ({ isGroup }: SummaryPageProps) => {
  const { t } = useTranslation();
  const { uuid } = useParams<{ uuid: string }>();
  const { classes } = useStyles();
  const history = useHistory<any>();
  const { state, dispatch: tableDispatch } = useDataTable();
  const [fileStatus, setFileStatus] = useState(FileUploadState.IDLE);

  const {
    data,
    isLoading,
  } = candidateService.accessArrangement.candidate.useGetAppealArrangementSummaryDetails(uuid);

  const {
    refetch: fetchSummaryPDF,
  } = candidateService.accessArrangement.candidate.useGetAppealArrangementSummaryPDF(
    uuid,
    (res) => {
      window.open(res?.url, '_blank');
    },
    (_) => {
      setFileStatus(FileUploadState.FAILED);
    }
  );

  const useGetAppealArrangementOutcomePDF = useGetArrangementOutcomePDF({
    onSuccess: (res) => {
      window.open(res?.url, '_blank');
    },
  });

  const columnOptions = useAccessArrangementSummaryColumns(state, classes);

// wogaa transaction start for aa amendment for candidate and coordinator //
  const { transactionStart } = useWOGAA(isGroup ? 'myexamsseab-6468' : 'myexamsseab-6466');

  const { mutate: postAccessArrangementAmendment } = usePostAccessArrangementAmendment({
    onSuccess: () => {
      transactionStart();
      handleAmendApplication(isGroup, history, uuid, data);
    },
  });

  useEffect(() => {
    if (data) {
      const accessArrangementRequestDetails = data?.accessArrangementRequestReviewModel.accessArrangementRequestDetailModels.filter(
        (accessArrangementRequestDetail) => !accessArrangementRequestDetail.isDraft
      );

      tableDispatch({
        type: 'UPDATE_DATA',
        payload: {
          data: accessArrangementRequestDetails,
          count: accessArrangementRequestDetails?.length,
        },
      } as TableActionTypes);
    }
  }, [data, tableDispatch]);

  if (isLoading || !data) {
    return (
      <ContentTemplate transparent continuous>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  const handleBack = () => {
    if (!isGroup) {
      history.push(`/${accessArrangement}/${root.route}`);
    } else {
      history.push(`/${accessArrangement}/${coordinatorRoot.route}`, {
        pageSize: history?.location?.state?.pageSize,
        page: history?.location?.state?.page,
      });
    }
  };

  const handleDownloadPdf = () => {
    fetchSummaryPDF();
  };

  const handleDownloadArrangementOutcomePdf = (fileKey: string) => () => {
    useGetAppealArrangementOutcomePDF.mutate({ fileKey });
  };

  const handleCloseDownloadErrorPrompt = () => {
    setFileStatus(FileUploadState.IDLE);
  };

  return (
    <>
      <TitleHeader title={t('accessArrangement->>Summary.Title')} />
      <ContentTemplate isDividerBottom isContinuable>
        <ApplicationDetails handleDownloadPdf={handleDownloadPdf} data={data} />
      </ContentTemplate>
      <ContentTemplate continuous isDividerBottom>
        <Grid className={classes.personalDetailsContainer}>
          <Typography variant="h2" className={classes.subheader}>
            {t('accessArrangement->>ReviewApplication.ApplicationOutcomes')}
          </Typography>
          <Grid className={classes.applicationOutcomeContainer}>
            {data.accessArrangementOutcomeModels.length
              ? data.accessArrangementOutcomeModels.map((document) => (
                <Grid
                  key={document.fileKey}
                  container
                  className={classes.applicationOutcomeRowContainer}
                >
                  <Grid md={10} sm={7} xs={7}>
                    <DocumentBadge
                      className={classes.documentOutcomeBadgeContainer}
                      documentName={document.fileName}
                    />
                  </Grid>
                  <Grid md={2} sm={5} xs={5} alignItems="center">
                    <Link
                      className={classes.downloadPdf}
                      id="link-download"
                      onClick={handleDownloadArrangementOutcomePdf(document.fileKey)}
                    >
                      <IconWithLabel
                        iconName={IconName.download}
                        iconSize="M"
                        label={t('exam->>RegistrationSummary.Download')}
                        typographyProps={{
                          variant: 'h5',
                        }}
                      />
                    </Link>
                  </Grid>
                </Grid>
              ))
              : '-'}
          </Grid>
        </Grid>
      </ContentTemplate>
      <ContentTemplate continuous isDividerBottom>
        <Grid className={classes.personalDetailsContainer}>
          {data.accessArrangementSubmittedDataModel.coordidateName ? (
            <Typography variant="h2" className={classes.subheader}>
              {t('accessArrangement->>ReviewApplication.SubheaderCoordinator1')}
            </Typography>
          ) : (
            <Typography variant="h2" className={classes.subheader}>
              {t('accessArrangement->>ReviewApplication.Subheader1')}
            </Typography>
          )}
          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="candidateName"
              keyText={t('accessArrangement->>ReviewApplication.Name')}
              valueText={
                data.accessArrangementRequestReviewModel.accessArrangementCandidateDetailsModel
                  .statutoryName
              }
            />
          </Grid>
          <Grid className={classes.keyValueContainer}>
            <KeyValue
              id="candidateNric"
              keyText={t('accessArrangement->>ReviewApplication.NRIC')}
              valueText={
                data.accessArrangementRequestReviewModel.accessArrangementCandidateDetailsModel
                  .nricFin
              }
            />
          </Grid>
        </Grid>
      </ContentTemplate>
      <ContentTemplate isDividerBottom continuous isContinuable type="large">
        <ReviewApplication data={data} />
      </ContentTemplate>
      <ContentTemplate continuous isContinuable type="full">
        <Typography variant="h2" className={classes.subheader}>
          {t('accessArrangement->>ReviewApplication.Subheader3')}
        </Typography>
        <Grid container>
          <Grid xs={12} md={6}>
            <KeyValue
              id="candidateName"
              keyText={t('accessArrangement->>ReviewApplication.Year')}
              valueText={
                data.accessArrangementRequestReviewModel.accessArrangementRequestModel.examYear
              }
            />
          </Grid>
          <Grid xs={12} md={6}>
            <KeyValue
              id="candidateName"
              capitalize={false}
              keyText={t('accessArrangement->>ReviewApplication.ExammLevel')}
              valueText={data.accessArrangementRequestReviewModel.accessArrangementRequestModel?.examLevels.join(
                ', '
              )}
            />
          </Grid>
        </Grid>
        <Grid>
          <MUIDataTable
            columns={columnOptions}
            state={state}
            data={state.data}
            options={getTableOptions(classes, t)}
            dispatch={tableDispatch}
          />
        </Grid>
      </ContentTemplate>
      <ContentTemplate continuous>
        <NavigationButtons
          backLabel={t('accessArrangement->>Summary.Back')}
          procceedLabel={t('accessArrangement->>Summary.Amend')}
          onBackClick={handleBack}
          onProceedClick={() => postAccessArrangementAmendment(uuid)}
          showProceedButton={data.amendable}
        />
      </ContentTemplate>
      {fileStatus === FileUploadState.FAILED && (
        <DownloadProgressModal
          modalState
          fileDownloadStatus={FileUploadState.FAILED}
          onDialogClose={handleCloseDownloadErrorPrompt}
        />
      )}
    </>
  );
};

export default SummaryPage;
